import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { CustomInput } from "../../components/CustomInput";
import { StyledButton } from "../../components/StyledButton";
import { ErrorMessage } from "src/components/ErrorMessage";

const FITTING_INSTRUCTION_API = process.env.REACT_APP_API_FITTING_INSTRUCTION;

export const SearchProduct = ({
  partNum,
  setPartNum,
  setSerialNum,
  setIsSearchSuccess,
  setAdditionalInfos,
  setFittingInfos,
  setProductDesc,
}) => {
  const [inputPartNumber, setInputPartNumber] = useState(partNum || "");
  const [errorMessage, setErrorMessage] = useState("");
  const [debouncedPartNumber, setDebouncedPartNumber] =
    useState(inputPartNumber);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputPartNumber(value);
    setPartNum(value);
    setSerialNum(""); //Clear serial number when inputPartNumber changed
  };

  const handleError = useCallback(
    (message) => {
      setIsSearchSuccess(false);
      setAdditionalInfos([]);
      setFittingInfos([]);
      setErrorMessage(message);
    },
    [setIsSearchSuccess, setAdditionalInfos, setFittingInfos]
  );

  // Debounce to update part number
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedPartNumber(inputPartNumber);
    }, 500); // Set 0.5s debounce
    return () => {
      clearTimeout(handler);
    };
  }, [inputPartNumber]);

  // Filters and extracts relevant information from an array of instructions based on specific keywords.
  const extractRelevantInfo = (instructions, ...keywords) => {
    return instructions
      .filter((instr) =>
        keywords.some((keyword) => instr.contentDescription.includes(keyword))
      )
      .map((info) => ({
        contentUrl: info.contentUrl,
        fileName: info.fileName,
        sortNumber: info.sortNumber,
        extension: info.extension,
      }));
  };

  const handleSearch = useCallback(async () => {
    if (debouncedPartNumber.trim() === "") {
      setErrorMessage(
        "Please enter a valid part number to see more information."
      );
      setIsSearchSuccess(false);
      return;
    }

    try {
      const response = await fetch(FITTING_INSTRUCTION_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ partNumber: debouncedPartNumber }),
      });

      // Check response.ok
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.ErrorFlag === "0") {
        const additionalInfos = extractRelevantInfo(
          data.FittingInstrutions,
          "Additional Information",
          "Product"
        );
        const fittingInfos = extractRelevantInfo(
          data.FittingInstrutions,
          "Fitting"
        );

        setProductDesc(data.JdeItemDescription);
        setIsSearchSuccess(true);
        setAdditionalInfos(additionalInfos);
        setFittingInfos(fittingInfos);
        setErrorMessage("");
      } else {
        handleError(
          "Unfortunately, the part number you searched for could not be found in our system. Please double check the number and try again. If you continue to have trouble locating the part, feel free to contact your local store or stockist for further assistance. You can find your nearest location using our Stores & Stockists Finder."
        );
      }
    } catch (error) {
      console.error("Fetch error:", error);
      handleError("Error fetching data.");
    }
  }, [
    debouncedPartNumber,
    setIsSearchSuccess,
    setAdditionalInfos,
    setFittingInfos,
    setProductDesc,
    handleError,
  ]); //Trigger useEffect when debouncedPartNumber(inputPartNumber) change

  useEffect(() => {
    if (debouncedPartNumber) {
      handleSearch();
    }
  }, [debouncedPartNumber, handleSearch]);

  //Trigger search when press enter on the keyboard
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <ProductSearchContainer>
      <h4>Search by Part Number</h4>
      <SearchInputContainer>
        <CustomInput
          placeholder="Insert Part Number"
          value={inputPartNumber}
          onChange={handleInputChange}
          className={errorMessage ? "error" : ""}
          onKeyDown={handleKeyDown}
        />
        <StyledButton
          className="button primary medium"
          type="submit"
          onClick={handleSearch}
        >
          Search
        </StyledButton>
      </SearchInputContainer>
      <ErrorMessage>{errorMessage}</ErrorMessage>
      {errorMessage && (
        <StyledButton
          className="button primary medium"
          onClick={() => {
            window.open(
              "https://www.arb.com.au/contact/find-your-nearest-store/",
              "_blank",
              "noopener,noreferrer"
            );
          }}
        >
          Find your nearest ARB store or stockist
        </StyledButton>
      )}
    </ProductSearchContainer>
  );
};
const ProductSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
`;

const SearchInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 20px;

  @media (min-width: 600px) {
    flex-direction: row;
  }
`;
